/**
 * Reminder System Integration Utilities
 */

import { getFirestore, doc, getDoc } from 'firebase/firestore';

class ReminderIntegration {
    constructor() {
        this.db = getFirestore();
        
        // Always use production Firebase Functions (no emulators)
        this.API_BASE_URL = 'https://us-central1-my-tassel.cloudfunctions.net/api';
    }

    /**
     * Schedule event reminders when RSVP widget is saved/updated
     * Uses Timeline event data as single source of truth
     */
    async scheduleRemindersOnRSVPUpdate(userEmail, rsvpCardData) {
        
        
        try {
            // Get Timeline event data via timelineEventId
            const userDoc = await getDoc(doc(this.db, 'users', userEmail));
            if (!userDoc.exists()) {
                
                return { success: false, reason: 'User not found' };
            }

            const userData = userDoc.data();
            const reminderSettings = userData.pages?.eventsPageConfig?.reminderSettings;
            
            if (!reminderSettings || !reminderSettings.enabled) {
                
                return { success: false, reason: 'Reminders not enabled' };
            }

            // Extract Timeline event data
            let eventData = null;
            
            if (rsvpCardData.timelineEventId) {
                // Get event from Timeline
                const cards = userData.pages?.eventsPageConfig?.cards || [];
                for (const card of cards) {
                    if (card.type === 'Timeline' && card.events) {
                        const event = card.events.find(e => e.id === rsvpCardData.timelineEventId);
                        if (event) {
                            eventData = {
                                title: event.title,
                                date: event.datetime,
                                location: event.location || '',
                                description: event.description || ''
                            };
                            break;
                        }
                    }
                }
            }
            
            if (!eventData) {
                
                return { success: false, reason: 'No linked Timeline event' };
            }
            
            // Schedule the reminders with testMode explicitly set to false
            const result = await this.callAPI('/schedule-event-reminders', {
                eventOwnerId: userEmail,
                eventData: eventData,
                testMode: false
            });
            
            
            return result;
            
        } catch (error) {
            
            return { success: false, error: error.message };
        }
    }

    /**
     * Update reminder schedules when Timeline event details change
     */
    async updateEventReminders(userEmail, updatedEventData) {
        
        
        try {
            const result = await this.callAPI('/reschedule-event-reminders', {
                eventOwnerId: userEmail,
                updatedEventData: updatedEventData
            });
            
            console.log('ReminderIntegration: Event reminders rescheduled successfully:', {
                canceled: result.canceled,
                rescheduled: result.rescheduled,
                recipients: result.recipientCount
            });
            
            return result;
            
        } catch (error) {
            
            return { success: false, error: error.message };
        }
    }

    /**
     * Cancel existing scheduled reminders for a user
     */
    async cancelExistingReminders(userEmail) {
        
        
        try {
            
            
            
            
        } catch (error) {
            
        }
    }

    /**
     * Schedule reminders when shared contacts are added
     * Uses Timeline event data via RSVP link
     */
    async onSharedContactsUpdate(userEmail) {
        
        
        try {
            const userDoc = await getDoc(doc(this.db, 'users', userEmail));
            if (!userDoc.exists()) {
                return { success: false, reason: 'User not found' };
            }
            
            const userData = userDoc.data();
            const eventsPageConfig = userData.pages?.eventsPageConfig;
            
            // Find RSVP card with Timeline link
            let eventData = null;
            const cards = eventsPageConfig?.cards || [];
            
            for (const card of cards) {
                if (card.type === 'RSVP' && card.timelineEventId) {
                    // Get Timeline event
                    for (const timelineCard of cards) {
                        if (timelineCard.type === 'Timeline' && timelineCard.events) {
                            const event = timelineCard.events.find(e => e.id === card.timelineEventId);
                            if (event) {
                                eventData = {
                                    title: event.title,
                                    date: event.datetime,
                                    location: event.location || '',
                                    description: event.description || ''
                                };
                                break;
                            }
                        }
                    }
                    if (eventData) break;
                }
            }
            
            if (eventData) {
                return await this.updateEventReminders(userEmail, eventData);
            } else {
                
                return { success: false, reason: 'No Timeline event linked' };
            }
            
        } catch (error) {
            
            return { success: false, error: error.message };
        }
    }

    /**
     * Get reminder status for dashboard display
     */
    async getReminderStatus(userEmail) {
        
        
        try {
            const result = await this.callAPI('/get-reminder-recipients', {
                eventOwnerId: userEmail,
                includeSharedContacts: true
            });
            
            return {
                success: true,
                recipientCount: result.summary.total,
                emailRecipients: result.summary.withEmail,
                smsRecipients: result.summary.withPhone,
                rsvpCount: result.summary.bySource.rsvp,
                sharedCount: result.summary.bySource.shared
            };
            
        } catch (error) {
            
            return { success: false, error: error.message };
        }
    }

    /**
     * Helper function to call Firebase Functions API
     */
    async callAPI(endpoint, data = {}) {
        try {
            const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();
            
            if (!response.ok) {
                throw new Error(result.error || `HTTP ${response.status}`);
            }

            return result;
        } catch (error) {
            
            throw error;
        }
    }

    /**
     * Validate reminder settings before scheduling
     */
    validateReminderSettings(reminderSettings) {
        if (!reminderSettings) {
            return { valid: false, reason: 'No reminder settings provided' };
        }
        
        if (!reminderSettings.enabled) {
            return { valid: false, reason: 'Reminders not enabled' };
        }
        
        if (!reminderSettings.reminderTimes || reminderSettings.reminderTimes.length === 0) {
            return { valid: false, reason: 'No reminder times configured' };
        }
        
        // Validate reminder time formats
        const validReminderTypes = ['day_of', 'days_before_1', 'days_before_3', 'days_before_7', 'days_before_10'];
        const invalidTypes = reminderSettings.reminderTimes.filter(type => !validReminderTypes.includes(type));
        
        if (invalidTypes.length > 0) {
            return { valid: false, reason: `Invalid reminder types: ${invalidTypes.join(', ')}` };
        }
        
        return { valid: true };
    }

    /**
     * Format event data for reminder scheduling
     * Supports both Timeline and legacy RSVP formats
     */
    formatEventDataForReminders(eventData) {
        return {
            title: eventData.title || eventData.eventTitle || 'Graduation Celebration',
            date: eventData.datetime || eventData.eventDate || eventData.date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            location: eventData.location || eventData.eventLocation || '',
            description: eventData.description || eventData.eventDescription || 'Join us for this special celebration!'
        };
    }
}

// Create singleton instance
const reminderIntegration = new ReminderIntegration();

// Export hooks for easy integration with existing components
export const useReminderIntegration = () => {
    return {
        scheduleRemindersOnRSVPUpdate: reminderIntegration.scheduleRemindersOnRSVPUpdate.bind(reminderIntegration),
        updateEventReminders: reminderIntegration.updateEventReminders.bind(reminderIntegration),
        onSharedContactsUpdate: reminderIntegration.onSharedContactsUpdate.bind(reminderIntegration),
        getReminderStatus: reminderIntegration.getReminderStatus.bind(reminderIntegration),
        validateReminderSettings: reminderIntegration.validateReminderSettings.bind(reminderIntegration),
        formatEventDataForReminders: reminderIntegration.formatEventDataForReminders.bind(reminderIntegration)
    };
};

// Export class for direct use
export { ReminderIntegration };

// Export singleton instance
export default reminderIntegration;